let refreshing = false;
let activatingWaiting = false;
let reloadThisClientOnControllerChange = false;
let pendingWorker: ServiceWorker | null = null;

export const pwaUpdateAvailable = ref(false);

function reloadWhenControlled(): void {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!reloadThisClientOnControllerChange || refreshing) {
            return;
        }

        pwaUpdateAvailable.value = false;
        pendingWorker = null;
        refreshing = true;
        window.setTimeout(() => {
            window.location.reload();
        }, 600);
    });
}

function activateWaiting(worker: ServiceWorker): void {
    activatingWaiting = true;
    reloadThisClientOnControllerChange = true;
    pendingWorker = worker;
    pwaUpdateAvailable.value = false;
    worker.postMessage({ type: 'SKIP_WAITING' });
}

function promptUpdate(worker: ServiceWorker): void {
    if (activatingWaiting) {
        return;
    }

    pendingWorker = worker;
    pwaUpdateAvailable.value = true;
}

export function applyPwaUpdate(): void {
    reloadThisClientOnControllerChange = true;

    if (!pendingWorker) {
        window.location.reload();
        return;
    }

    pendingWorker.postMessage({ type: 'SKIP_WAITING' });

    // If SW was already activated from another tab, controllerchange may not fire again
    window.setTimeout(() => {
        if (!refreshing) {
            refreshing = true;
            window.location.reload();
        }
    }, 800);
}

export function dismissPwaUpdate(): void {
    pwaUpdateAvailable.value = false;
}

function watchForUpdates(registration: ServiceWorkerRegistration): void {
    if (registration.waiting && navigator.serviceWorker.controller) {
        activateWaiting(registration.waiting);
    }

    registration.addEventListener('updatefound', () => {
        const worker = registration.installing;

        if (!worker) {
            return;
        }

        worker.addEventListener('statechange', () => {
            if (worker.state === 'installed' && navigator.serviceWorker.controller) {
                promptUpdate(worker);
            }
        });
    });
}

function scheduleUpdateChecks(registration: ServiceWorkerRegistration): void {
    const checkForUpdate = () => {
        registration.update().catch((error) => {
            console.warn('[PWA] Service worker update check failed', error);
        });

        if (activatingWaiting) {
            return;
        }

        if (registration.waiting && navigator.serviceWorker.controller) {
            promptUpdate(registration.waiting);
        }
    };

    checkForUpdate();
    window.addEventListener('focus', checkForUpdate);
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            checkForUpdate();
        }
    });
    window.setInterval(checkForUpdate, 60 * 60 * 1000);
}

export function registerServiceWorker(): void {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) {
        return;
    }

    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register('/sw.js', { scope: '/', updateViaCache: 'none' })
            .then((registration) => {
                reloadWhenControlled();
                watchForUpdates(registration);
                scheduleUpdateChecks(registration);
            })
            .catch((error) => {
                console.warn('[PWA] Service worker registration failed', error);
            });
    });
}
